/**
 * Quota presentation (prompt.md §32, §33, §5).
 *
 * WHY: a plan quota is the one number that can pause a run, so the UI has to
 * explain three things consistently everywhere it appears — how much is used,
 * what the ceiling is, and whether that ceiling is a hard one. `null` means
 * "unlimited" throughout the entitlement system, and it must never be rendered
 * as "0".
 */

import type { StatusTone } from "./runState.ts";
import { formatCount } from "./format.ts";

export interface UsageMeterView {
  readonly used: number;
  /** `null` means unlimited, not zero. */
  readonly limit: number | null;
  /** Fraction of the limit used, or `null` when unlimited. May exceed 1. */
  readonly ratio: number | null;
  readonly percent: number | null;
  readonly usedText: string;
  readonly limitText: string;
  readonly remainingText: string;
  readonly tone: StatusTone;
  /** Screen-reader text, so a bar is never colour-only information. */
  readonly ariaValueText: string;
}

/** Tone thresholds: warning at 80%, danger once the limit is reached. */
export function usageTone(ratio: number | null): StatusTone {
  if (ratio === null) return "accent";
  if (ratio >= 1) return "danger";
  if (ratio >= 0.8) return "warning";
  return "accent";
}

export function usageMeterView(input: {
  readonly used: number;
  readonly limit: number | null;
  readonly format?: (value: number) => string;
}): UsageMeterView {
  const format = input.format ?? formatCount;
  const used = Number.isFinite(input.used) ? Math.max(0, input.used) : 0;
  const limit = input.limit === null ? null : Math.max(0, input.limit);
  const ratio = limit === null || limit === 0 ? (limit === 0 ? 1 : null) : used / limit;
  const percent = ratio === null ? null : Math.round(ratio * 100);
  const usedText = format(used);
  const limitText = limit === null ? "unlimited" : format(limit);
  const remainingText = limit === null ? "unlimited" : format(Math.max(0, limit - used));
  return {
    used,
    limit,
    ratio,
    percent,
    usedText,
    limitText,
    remainingText,
    tone: usageTone(ratio),
    ariaValueText: limit === null ? `${usedText} used, no limit on this plan` : `${usedText} of ${limitText} used`,
  };
}

/**
 * A quota warning that distinguishes "you are close" from "this is what paused
 * your run", because a paused run must read as recoverable (prompt.md §21).
 */
export function quotaNotice(view: UsageMeterView, subject: string): string | null {
  if (view.ratio === null) return null;
  if (view.ratio >= 1) return `${subject} limit reached — new runs will pause rather than fail`;
  if (view.ratio >= 0.8) return `${subject} is almost used up`;
  return null;
}
